import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';

import { EMAIL_CARD, RAAS_EMAIL, RAAS_MAILTO } from '../../constants';

import styles from './styles.module.css';

import { track } from '@/shared/analytics/track';

/** How long the copy button shows its confirmed state before reverting. */
const COPIED_RESET_MS = 2000;

const EmailCard: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | null>(null);

  useEffect(
    () => (): void => {
      if (resetTimer.current !== null) {
        window.clearTimeout(resetTimer.current);
      }
    },
    []
  );

  const confirmCopied = (): void => {
    track('contact_email_copied', { location: 'contact_email_card' });
    setCopied(true);
    if (resetTimer.current !== null) {
      window.clearTimeout(resetTimer.current);
    }
    resetTimer.current = window.setTimeout(
      () => setCopied(false),
      COPIED_RESET_MS
    );
  };

  // Promise-chained rather than async/await so the handler itself returns void
  // and needs no `void` operator at the call site (this repo's ESLint forbids
  // it). The rejection path is deliberately silent: clipboard writes reject in
  // insecure contexts and when permission is denied, and confirming a copy that
  // never happened is worse than doing nothing — the address stays selectable.
  const handleCopy = (): void => {
    navigator.clipboard.writeText(RAAS_EMAIL).then(confirmCopied, () => {});
  };

  return (
    <div className={styles.card}>
      <span className={styles.kicker}>{EMAIL_CARD.kicker}</span>
      <h2 className={styles.title}>{EMAIL_CARD.title}</h2>
      <p className={styles.body}>{EMAIL_CARD.body}</p>

      <div className={styles.addressRow}>
        {/* Both controls carry their hint as `data-hint`, revealed on hover and
            on keyboard focus by CSS. Deliberately not shared/ui/Tooltip: that
            component runs useLayoutEffect and measures the DOM, which warns
            under renderToString — and this page is prerendered. */}
        {/* Copying was already tracked but clicking through to a mail client
            was not, so the card looked half as effective as it is. */}
        <a
          className={styles.mailto}
          href={RAAS_MAILTO}
          onClick={() =>
            track('contact_email_clicked', { location: 'contact_email_card' })
          }
          data-hint={EMAIL_CARD.mailtoHint}
          aria-label={`${RAAS_EMAIL} — ${EMAIL_CARD.mailtoHint}`}
        >
          <Mail size={26} strokeWidth={1.5} aria-hidden="true" />
          {RAAS_EMAIL}
        </a>

        <button
          type="button"
          className={styles.copyButton}
          onClick={handleCopy}
          data-hint={EMAIL_CARD.copyHint}
          aria-label={EMAIL_CARD.copyHint}
        >
          {copied ? (
            <Check size={22} strokeWidth={2.2} aria-hidden="true" />
          ) : (
            <Copy size={22} strokeWidth={1.6} aria-hidden="true" />
          )}
        </button>

        {/* Announced to screen readers; the icon swap covers sighted users. */}
        <span className={styles.srOnly} role="status">
          {copied ? EMAIL_CARD.copiedLabel : ''}
        </span>
      </div>

      <p className={styles.hours}>{EMAIL_CARD.hours}</p>
    </div>
  );
};

export default EmailCard;
